import React, { useEffect, useState } from "react";
import { TL } from "../i18n";
import client from "../lib/api";
import { Modal, Badge, Button } from "./ui";
import { fmtSchedule } from "./CustomFields";
import { CalendarClock, History, RefreshCw, Pause, Play, SkipForward } from "lucide-react";

const RECUR_LABEL = { once: "One-time", daily: "Daily", weekly: "Weekly", monthly: "Monthly", quarterly: "Quarterly", yearly: "Yearly" };
const KIND_LABEL = { equity_dividend: "Dividend", lending_income: "Lending income", lending_outlay: "Lending outlay" };
const money = (v) => (v == null || v === "" ? "—" : Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 }));
const statusBadge = (s) => {
  if (s === "executed") return <Badge color="success">{TL("Executed")}</Badge>;
  if (s === "skipped_insufficient") return <Badge color="danger">{TL("Insufficient funds")}</Badge>;
  if (s === "skipped_no_pool") return <Badge color="danger">{TL("No pool")}</Badge>;
  if (s === "skipped_no_amount") return <Badge color="info">{TL("No amount")}</Badge>;
  return s ? <Badge color="info">{s}</Badge> : null;
};

// Shows a user's upcoming scheduled runs and past executions for Currencida / SEBAA.
export function ScheduledActivity({ app, open, onClose }) {
  const [data, setData] = useState({ upcoming: [], past: [] });
  const [loading, setLoading] = useState(false);
  const isCur = app === "currency";
  const isEq = app === "equity";
  const load = () => {
    setLoading(true);
    client.get(`/scheduled-activity?app=${app}`).then((r) => setData(r.data || { upcoming: [], past: [] }))
      .catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open, app]);
  const runNow = () => { client.post("/scheduler/run-now").then(() => setTimeout(load, 600)).catch(() => {}); };
  const act = (path, body) => client.post(path, body).then(() => setTimeout(load, 400)).catch(() => {});
  const togglePause = (u) => act("/scheduler/pause", { app, id: u.id, paused: !u.paused });
  const skip = (u) => act("/scheduler/skip", { app, id: u.id });

  const proj = (u) => {
    const dNext = isCur ? u.debit_next : u.debit;
    return { dNext, balNext: u.bal_after_next, dNext2: u.debit_next2, balNext2: u.bal_after_next2 };
  };

  return (
    <Modal open={open} onClose={onClose} title={isEq ? "Scheduled equity legs" : "Scheduled activity"} testid="scheduled-activity" wide>
      <div className="space-y-6">
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={runNow} data-testid="sched-run-now"><RefreshCw size={14} /> {TL("Run due now")}</Button>
          <Button variant="ghost" onClick={load} data-testid="sched-refresh">{TL("Refresh")}</Button>
        </div>

        <section data-testid="sched-upcoming">
          <h4 className="mb-2 flex items-center gap-2 text-sm font-semibold opacity-80"><CalendarClock size={15} /> Upcoming ({data.upcoming.length})</h4>
          {data.upcoming.length === 0 ? (
            <p className="text-sm opacity-50">{isEq ? "No upcoming automated equity legs. Set a Dividend (Income) or Lending group's Mode to “Automated” or “Rule” to schedule it." : "No upcoming scheduled runs. Set a Date scheduler on a record to automate it."}</p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-black/10 dark:border-white/10">
              <table className="w-full text-sm">
                <thead className="bg-black/5 dark:bg-white/5 text-start text-xs uppercase opacity-60">
                  <tr>
                    <th className="p-2">{isCur ? "Pool" : isEq ? "Equity leg" : "Benefit"}</th>
                    <th className="p-2">{TL("Next run")}</th>
                    <th className="p-2">{TL("Then (day after)")}</th>
                    <th className="p-2">{TL("Repeats")}</th>
                    <th className="p-2 text-end">{TL("Actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.upcoming.map((u) => {
                    const p = proj(u);
                    const sign = u.direction === "credit" ? "+" : "−";
                    return (
                      <tr key={u.id} className={`border-t border-black/5 dark:border-white/5 ${u.paused ? "opacity-50" : ""}`} data-testid={`sched-upcoming-${u.id}`}>
                        <td className="p-2 font-medium">
                          {u.name}
                          {u.paused && <Badge color="neutral">{TL("Paused")}</Badge>}
                          {u.last_status && u.last_status !== "executed" && <div className="mt-0.5">{statusBadge(u.last_status)}</div>}
                        </td>
                        <td className="p-2 whitespace-nowrap">
                          <div>{u.next || "—"}</div>
                          <div className="text-xs opacity-60">{sign}{money(p.dNext)} → <b>{money(p.balNext)}</b></div>
                        </td>
                        <td className="p-2 whitespace-nowrap">
                          <div>{u.next2 || <span className="opacity-40">{TL("— (one-time)")}</span>}</div>
                          {u.next2 && <div className="text-xs opacity-60">{sign}{money(p.dNext2)} → <b>{money(p.balNext2)}</b></div>}
                        </td>
                        <td className="p-2">{RECUR_LABEL[u.recur] || u.recur}</td>
                        <td className="p-2">
                          <div className="flex justify-end gap-1">
                            <Button variant="ghost" onClick={() => togglePause(u)} data-testid={`sched-pause-${u.id}`} title={u.paused ? "Resume" : "Pause"}>
                              {u.paused ? <Play size={14} /> : <Pause size={14} />}{u.paused ? "Resume" : "Pause"}
                            </Button>
                            <Button variant="ghost" onClick={() => skip(u)} data-testid={`sched-skip-${u.id}`} title={TL("Skip next occurrence")}>
                              <SkipForward size={14} /> Skip
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section data-testid="sched-past">
          <h4 className="mb-2 flex items-center gap-2 text-sm font-semibold opacity-80"><History size={15} /> Past runs ({data.past.length})</h4>
          {data.past.length === 0 ? (
            <p className="text-sm opacity-50">{loading ? "Loading…" : "No runs yet."}</p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-black/10 dark:border-white/10">
              <table className="w-full text-sm">
                <thead className="bg-black/5 dark:bg-white/5 text-start text-xs uppercase opacity-60">
                  {isCur ? (
                    <tr><th className="p-2">{TL("Date")}</th><th className="p-2">{TL("Pool")}</th><th className="p-2">{TL("From → To")}</th><th className="p-2">{TL("Debited")}</th><th className="p-2">{TL("Balance")}</th><th className="p-2">{TL("Status")}</th></tr>
                  ) : isEq ? (
                    <tr><th className="p-2">{TL("Date")}</th><th className="p-2">{TL("Leg")}</th><th className="p-2">{TL("Equity")}</th><th className="p-2">{TL("Pool")}</th><th className="p-2 text-end">{TL("Amount")}</th></tr>
                  ) : (
                    <tr><th className="p-2">{TL("Date")}</th><th className="p-2">{TL("Benefit")}</th><th className="p-2">{TL("Pool")}</th><th className="p-2">{TL("Debited")}</th><th className="p-2">{TL("Balance")}</th></tr>
                  )}
                </thead>
                <tbody>
                  {data.past.map((p) => isCur ? (
                    <tr key={p.id} className="border-t border-black/5 dark:border-white/5" data-testid={`sched-past-${p.id}`}>
                      <td className="p-2 whitespace-nowrap">{(p.date || "").slice(0, 10)}</td>
                      <td className="p-2 font-medium">{p.currency_name}</td>
                      <td className="p-2 text-xs">{p.from_account || "—"} → {p.to_account || "—"}{p.credited !== p.debit ? ` (→ ${money(p.credited)} ${p.target_currency || ""})` : ""}</td>
                      <td className="p-2">{money(p.debit)}</td>
                      <td className="p-2">{money(p.balance_after)}</td>
                      <td className="p-2">{statusBadge(p.status)}</td>
                    </tr>
                  ) : isEq ? (
                    <tr key={p.id} className="border-t border-black/5 dark:border-white/5" data-testid={`sched-past-${p.id}`}>
                      <td className="p-2 whitespace-nowrap">{(p.at || p.created_at || "").slice(0, 10)}</td>
                      <td className="p-2 font-medium">{KIND_LABEL[p.kind] || p.kind}</td>
                      <td className="p-2">{p.meta?.ticker || "—"}</td>
                      <td className="p-2">{p.pool_name || "—"}</td>
                      <td className={`p-2 text-end font-semibold tabular-nums ${p.direction === "credit" ? "text-green-700 dark:text-green-400" : "text-red-600 dark:text-red-400"}`}>{p.direction === "credit" ? "+" : "−"}{money(p.amount)}</td>
                    </tr>
                  ) : (
                    <tr key={p.id} className="border-t border-black/5 dark:border-white/5" data-testid={`sched-past-${p.id}`}>
                      <td className="p-2 whitespace-nowrap">{(p.date || "").slice(0, 10)}</td>
                      <td className="p-2 font-medium">{p.benefit_name}</td>
                      <td className="p-2">{p.currency_name}</td>
                      <td className="p-2">{money(p.total_debit)}</td>
                      <td className="p-2">{money(p.currency_balance_after)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </Modal>
  );
}
